import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';

import { useMutation, useQuery } from '@apollo/client';

import { Button } from 'core/components/button';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import { sweepAccountFieldPermissions } from 'core/helpers/sweep-account';
import CollapsibleSection from './collapsible-section';
import PaymentMethodFields from './payment-method-fields';
import { useSweepPaymentToken } from './use-sweep-payment-token';
import { CREATE_SWEEP_ACCOUNT, GET_SWEEP_ACCOUNT_PREFILL } from './sweep-account.queries';
import { buildPrefillInitialValues, isFieldLocked } from './setup-form.helpers';
import useStyles from './sweep-account.styles';

const validate = (values) => {
  const errors = {};
  if (!values.name) {
    errors.name = 'Required';
  }
  if (!values.email) {
    errors.email = 'Required';
  }
  return errors;
};

const buildContact = (values) => ({
  name: values.name,
  email: values.email,
  phone: values.phone || null,
  address: values.line1
    ? {
        line1: values.line1,
        line2: values.line2 || null,
        city: values.city,
        state: values.state,
        zip: values.zip
      }
    : null
});

const SetupForm = ({ onCreated }) => {
  const classes = useStyles();
  const [createSweepAccount] = useMutation(CREATE_SWEEP_ACCOUNT);
  const getPaymentToken = useSweepPaymentToken();
  const [error, setError] = useState(null);
  // Pre-fill from the agency record we already hold (#33822) — a best-effort enrichment that
  // must never block the form. We render immediately with blank/editable defaults and patch in
  // the known (locked) values once the query resolves (enableReinitialize below).
  const { data: prefillData } = useQuery(GET_SWEEP_ACCOUNT_PREFILL, { fetchPolicy: 'no-cache' });
  const prefill = prefillData?.getSweepAccountPrefill ?? null;

  const onSubmit = async (values, { setSubmitting }) => {
    setError(null);
    try {
      const { paymentMethodToken, paymentMethodType } = await getPaymentToken(values.paymentMethodType);
      const { data } = await createSweepAccount({
        variables: { contact: buildContact(values), paymentMethodToken, paymentMethodType }
      });
      onCreated(data.createSweepAccount);
    } catch (e) {
      setError(e.message || 'Failed to create sweep account');
    } finally {
      setSubmitting(false);
    }
  };

  // fast={false} on the lockable fields: their disabled state flips when the prefill query
  // resolves, which Formik's FastField would not re-render for.
  return (
    <Formik
      enableReinitialize
      initialValues={buildPrefillInitialValues(prefill)}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <Form className={classes.form}>
          <CollapsibleSection title="Agency Details*">
            <FormField
              permissions={sweepAccountFieldPermissions}
              xs={6}
              type="string"
              name="name"
              mode="light"
              label="Agency Name*"
              fast={false}
              disabled={isFieldLocked('name', prefill)}
            />
            <FormField
              permissions={sweepAccountFieldPermissions}
              xs={6}
              type="string"
              name="email"
              mode="light"
              label="Email*"
            />
            <FormField
              permissions={sweepAccountFieldPermissions}
              xs={6}
              type="string"
              name="phone"
              mode="light"
              label="Phone"
            />
            <FormField
              permissions={sweepAccountFieldPermissions}
              xs={6}
              type="string"
              name="line1"
              mode="light"
              label="Address Line 1"
              fast={false}
              disabled={isFieldLocked('line1', prefill)}
            />
            <FormField
              permissions={sweepAccountFieldPermissions}
              xs={12}
              type="string"
              name="line2"
              mode="light"
              label="Address Line 2"
            />
            <FormField
              permissions={sweepAccountFieldPermissions}
              xs={4}
              type="string"
              name="city"
              mode="light"
              label="City"
              fast={false}
              disabled={isFieldLocked('city', prefill)}
            />
            <FormField
              permissions={sweepAccountFieldPermissions}
              xs={4}
              type="string"
              name="state"
              mode="light"
              label="State"
              fast={false}
              disabled={isFieldLocked('state', prefill)}
            />
            <FormField
              permissions={sweepAccountFieldPermissions}
              xs={4}
              type="string"
              name="zip"
              mode="light"
              label="Zip Code"
              fast={false}
              disabled={isFieldLocked('zip', prefill)}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Payment Method*">
            <PaymentMethodFields />
          </CollapsibleSection>

          {error && (
            <Label type="infoValue" className={classes.error}>
              {error}
            </Label>
          )}
          <div className={classes.actions}>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              className={classes.submitButton}
              loading={isSubmitting}
            >
              Create sweep account
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

SetupForm.propTypes = {
  onCreated: PropTypes.func.isRequired
};

export default SetupForm;
